import { CommonModule } from "@angular/common";
import {
  Component,
  inject,
  Inject,
  OnDestroy,
  Optional,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {
  MatNativeDateModule,
  provideNativeDateAdapter,
} from "@angular/material/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatStepper } from "@angular/material/stepper";
import { ConnectedPosition } from "@angular/cdk/overlay";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TablerIconsModule } from "angular-tabler-icons";
import { catchError, EMPTY, finalize, of, switchMap, tap } from "rxjs";
import { AppConfigService } from "src/app/core/appConfigs/app-config.service";
import { TourAfterLinkingStoreService } from "src/app/core/tours-cdk/toursservices/tour-after-linking-store.service";
import { TourAfterStoreLinkingAddDeliveryService } from "src/app/core/tours-cdk/toursservices/tour-after-store-linking-add-delivery.service";
import { TourAfterStoreLinkingAddThresholdService } from "src/app/core/tours-cdk/toursservices/tour-after-store-linking-add-threshold.service";
import { MaterialModule } from "src/app/material.module";
import { isUserOnboardingInProgress } from "src/app/pages/profile/profile.model";
import { ProfileService } from "src/app/pages/profile/profile.service";
import { ProviderClaimTypesGetDTO } from "src/app/pages/providers/provider.model";
import { ClaimType } from "src/app/pages/store-management/claims/claim-model";
import { StoreDetailsHelpPopoverComponent } from "./popovers/store-details-help-popover.component";
import { StoreDeliveryHelpPopoverComponent } from "./popovers/store-delivery-help-popover.component";
import { StoreThresholdHelpPopoverComponent } from "./popovers/store-threshold-help-popover.component";
import { StoreService } from "src/app/services/apps/store/store.service";
import { LoaderService } from "src/app/shared/components/loader/loader.service";
import { PopoverComponent } from "src/app/shared/components/treeslo-grid/popover/popover.component";
import { ColCountDirective } from "src/app/shared/directives/col-count.directive";
import {
  eventAction,
  ViewContextHistoryStateService,
} from "src/app/shared/services/view-context-history-state.service";
import {
  StoreConfigResponse,
  StoreDeliveryPayload,
  StoreGetDTO,
  StoreThresholdPayload,
  StoreUpdateDTO,
} from "../store";

type StoreDialogViewModelState = {
  formValue: StoreGetDTO;
};

type StoreUpdateDialogMode = "openAfterLinking" | "onlyCompleteSetup";

interface DialogData {
  store: StoreGetDTO;
  parentRoute: any;
  mode?: StoreUpdateDialogMode;
  claimTypes: ProviderClaimTypesGetDTO[];
  storeConfig?: StoreConfigResponse;
}

@Component({
  standalone: true,
  selector: "app-store-update-dialog",
  templateUrl: "./store-update-dialog.component.html",
  styleUrls: ["./store-update-dialog.component.scss"],
  imports: [
    MaterialModule,
    ReactiveFormsModule,
    CommonModule,
    MatNativeDateModule,
    TablerIconsModule,
    TranslateModule,
    PopoverComponent,
    StoreDetailsHelpPopoverComponent,
    StoreDeliveryHelpPopoverComponent,
    StoreThresholdHelpPopoverComponent,
  ],
  providers: [provideNativeDateAdapter()],
  encapsulation: ViewEncapsulation.None,
})
export class StoreUpdateDialogComponent implements OnDestroy {
  local_data: StoreGetDTO;
  parentRouteSnapshot: any;
  form: FormGroup;
  deliveryForm: FormGroup;
  thresholdForm: FormGroup;
  mode?: StoreUpdateDialogMode;
  isMultiStepMode = false;
  showStoreStep = false;
  enableDeliveryAddressStep = true;
  claimTypesForProvider: ProviderClaimTypesGetDTO[] = [];
  submitCount = 0;
  submitHistory = new Map<string, string>();
  private closeRefreshOverride = false;
  private shouldCacheInitialThresholdValue = false;
  deliverySubmitted = false;
  thresholdSubmitted = false;

  // Popover components
  readonly StoreDetailsHelpPopoverComponent = StoreDetailsHelpPopoverComponent;
  readonly StoreDeliveryHelpPopoverComponent = StoreDeliveryHelpPopoverComponent;
  readonly StoreThresholdHelpPopoverComponent = StoreThresholdHelpPopoverComponent;

  readonly popoverPositions: ConnectedPosition[] = [
    {
      originX: "start",
      originY: "bottom",
      overlayX: "start",
      overlayY: "top",
      offsetY: 8,
    },
    {
      originX: "end",
      originY: "bottom",
      overlayX: "end",
      overlayY: "top",
      offsetY: 8,
    },
  ];

  @ViewChild("stepper") stepper?: MatStepper;

  translate = inject(TranslateService);
  private fb = inject(FormBuilder);
  private loaderSvc = inject(LoaderService);
  private viewContextHistorySvc: ViewContextHistoryStateService<StoreDialogViewModelState> =
    inject(ViewContextHistoryStateService);
  private tourAfterStoreLinking = inject(TourAfterLinkingStoreService);
  private tourAfterStoreLinkingAddDelivery = inject(
    TourAfterStoreLinkingAddDeliveryService,
  );
  private tourAfterStoreLinkingAddThreshold = inject(
    TourAfterStoreLinkingAddThresholdService,
  );
  hasSeenTourAfterLinkingStore = false;
  hasSeenTourAfterStoreLinkingAddDelivery = false;
  hasSeenTourAfterStoreLinkingAddTreshold = false;
  private appConfigService = inject(AppConfigService);
  private profileService = inject(ProfileService);
  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<StoreUpdateDialogComponent>,
    private storeService: StoreService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: DialogData,
  ) {
    this.local_data = { ...data?.store } as StoreGetDTO;
    this.parentRouteSnapshot = data?.parentRoute;
    this.mode = data?.mode;
    this.isMultiStepMode = Boolean(this.mode);
    this.showStoreStep = this.mode === "openAfterLinking";
    this.claimTypesForProvider =
      data?.claimTypes.filter(
        (claimType) =>
          claimType.provider == this.local_data.provider &&
          claimType.value !== ClaimType.Unknown,
      ) ?? [];

    if (this.isMultiStepMode) {
      this.dialogRef.beforeClosed().subscribe((result) => {
        if (
          !this.closeRefreshOverride &&
          this.submitCount > 0 &&
          !(result && (result as any).event === "Refresh")
        ) {
          this.closeRefreshOverride = true;
          this.dialogRef.close({ event: "Refresh" });
        }
      });
    }

    this.form = this.fb.group({
      name: [this.local_data.name ?? "", Validators.required],
      provider: [this.local_data.provider ?? ""],
      providerStoreId: [this.local_data.providerStoreId ?? ""],
      updatedOn: [this.local_data.updatedOn ?? ""],
      linkedAt: [this.local_data.linkedAt ?? ""],
      userId: [this.local_data.userId ?? ""],
      storeId: [this.local_data.storeId ?? ""],
      hasAutoRemove: [this.local_data.hasAutoRemove ?? false],
    });

    this.deliveryForm = this.fb.group({
      city: ["", Validators.required],
      state: ["", Validators.required],
      postalCode: ["", Validators.required],
    });

    this.thresholdForm = this.fb.group({});
    this.claimTypesForProvider.forEach((claimType) => {
      this.thresholdForm.addControl(
        claimType.value,
        this.fb.group({
          thresholdAmount: [0, [Validators.required, Validators.min(0)]],
          thresholdPercentage: [
            0,
            [Validators.required, Validators.min(0), Validators.max(100)],
          ],
        }),
      );
    });

    this.populateConfig(data?.storeConfig);
    this.shouldCacheInitialThresholdValue = this.hasStoredThresholdConfig(
      data?.storeConfig,
    );

    this.cacheFormValue("store", this.form.value);
    this.cacheFormValue("delivery", this.deliveryForm.value);
    if (this.shouldCacheInitialThresholdValue) {
      this.cacheFormValue("threshold", this.thresholdForm.value);
    }
  }

  ngOnInit(): void {
    // Read enableDeliveryAddressStep from appConfigs (always provided by backend)
    this.enableDeliveryAddressStep =
      this.appConfigService.appConfigs()?.enableDeliveryAddressStep!;

    this.setViewState(
      "modal_opened",
      `Modal opened in 'openAfterLinking' mode for store with id: ${this.local_data.storeId}`,
    );
    //start of onboarding flow after linking a store
    if (this.mode === "openAfterLinking") {
      if (
        !this.tourAfterStoreLinking.isRunning() &&
        !this.hasSeenTourAfterLinkingStore &&
        this.isFirstStoreOnboarding()
      ) {
        this.hasSeenTourAfterLinkingStore = true;
        this.tourAfterStoreLinking.start();
      }
    }

    if (this.mode === "onlyCompleteSetup") {
      if (this.isMultiStepMode) {
        if (this.isFirstStoreOnboarding()) {
          if (this.enableDeliveryAddressStep) {
            if (
              !this.tourAfterStoreLinkingAddDelivery.isRunning() &&
              !this.hasSeenTourAfterStoreLinkingAddDelivery
            ) {
              this.hasSeenTourAfterStoreLinkingAddDelivery = true;
              this.tourAfterStoreLinkingAddDelivery.start();
            }
          } else {
            if (
              !this.tourAfterStoreLinkingAddThreshold.isRunning() &&
              !this.hasSeenTourAfterStoreLinkingAddTreshold
            ) {
              this.hasSeenTourAfterStoreLinkingAddTreshold = true;
              this.tourAfterStoreLinkingAddThreshold.start();
            }
          }
        }
      }
    }
  }
  //
  setViewState(eventAction: eventAction, eventDescription: string) {
    this.viewContextHistorySvc.createContext({
      titleView: `Modal Update Store`,
      eventAction: eventAction,
      eventDescription: eventDescription,
      thisState: {
        formValue: { ...this.local_data },
      },
      route: this.parentRouteSnapshot,
      setAutomaticallyScreenshot: false,
    });
  }

  ngAfterViewInit(): void {
    this.setViewState(
      "modal_ready",
      `Modal for update on store with id: ${this.local_data?.storeId} initialized`,
    );
  }

  submitUpdate(advanceStepper?: MatStepper) {
    if (this.form.invalid) {
      return;
    }

    this.submitCount += 1;

    const updatedStore: StoreUpdateDTO = {
      ...this.form.value,
    };

    this.loaderSvc.show();
    this.storeService
      .update(this.local_data.storeId, updatedStore, {
        message: this.translate.instant("messages.Store Updated successfully!"),
        severity: "success",
        duration: 3000,
      })
      .pipe(
        tap(() => {
          this.setViewState(
            "form_submitted", `User submitted form to update store with name: ${updatedStore.name}`,
          );
          this.cacheFormValue("store", this.form.value);
          this.local_data = { ...this.local_data, ...updatedStore };
          if (!this.isMultiStepMode) {
            this.dialogRef.close({ event: "Refresh" });
            this.setViewState(
              "modal_completed", `User closed the modal after updating store with name: ${updatedStore.name}`,
            );
          } else if (advanceStepper) {
            this.onNextFromStoreStep(advanceStepper);
          }
        }),
        catchError((error) => {
          console.error("Error updating store:", error);
          this.setViewState(
            "form_submit_failed", `User attempted to submit form to update store with name: ${updatedStore.name} but an error occurred: ${error.message}`,
          );
          return EMPTY;
        }),
        finalize(() => {
          this.loaderSvc.hide();
        }),
      )
      .subscribe();
  }

  submitStoreStep(stepper: MatStepper): void {
    this.submitUpdate(stepper);
  }

  onNextFromStoreStep(stepper: MatStepper): void {
    stepper.next();

    // If delivery step is disabled, skip to threshold step
    if (!this.enableDeliveryAddressStep) {
      stepper.next();
    }

    if (this.isMultiStepMode && this.showStoreStep) {
      setTimeout(() => {
        if (this.isFirstStoreOnboarding()) {
          if (this.enableDeliveryAddressStep) {
            if (
              !this.tourAfterStoreLinkingAddDelivery.isRunning() &&
              !this.hasSeenTourAfterStoreLinkingAddDelivery
            ) {
              this.hasSeenTourAfterStoreLinkingAddDelivery = true;
              this.tourAfterStoreLinkingAddDelivery.start();
            }
          } else {
            if (
              !this.tourAfterStoreLinkingAddThreshold.isRunning() &&
              !this.hasSeenTourAfterStoreLinkingAddTreshold
            ) {
              this.hasSeenTourAfterStoreLinkingAddTreshold = true;
              this.tourAfterStoreLinkingAddThreshold.start();
            }
          }
        }
      }, 500);
    }
  }

  onNextFromDeliveryStep(stepper: MatStepper): void {
    // Only allow navigation if delivery has been successfully submitted
    if (!this.deliverySubmitted) {
      return;
    }

    stepper.next();

    if (this.isMultiStepMode) {
      setTimeout(() => {
        if (
          !this.tourAfterStoreLinkingAddThreshold.isRunning() &&
          !this.hasSeenTourAfterStoreLinkingAddTreshold &&
          this.isFirstStoreOnboarding()
        ) {
          this.hasSeenTourAfterStoreLinkingAddTreshold = true;
          this.tourAfterStoreLinkingAddThreshold.start();
        }
      }, 500);
    }
  }

  submitDeliveryStep() {
    if (this.deliveryForm.invalid) {
      return;
    }

    this.submitCount += 1;

    const deliveryPayload: StoreDeliveryPayload = {
      city: this.deliveryForm.value.city,
      state: this.deliveryForm.value.state,
      postalCode: this.deliveryForm.value.postalCode,
    };

    this.loaderSvc.show();
    this.storeService
      .updateDelivery(this.local_data.storeId, deliveryPayload)
      .pipe(
        tap(() => {
          this.setViewState(
            "form_submitted", `User submitted delivery payload for store with id: ${this.local_data.id}`,
          );
          this.cacheFormValue("delivery", this.deliveryForm.value);
          this.deliverySubmitted = true;

          // Auto advance to next step
          if (this.stepper) {
            this.stepper.next();
          }

          if (this.isMultiStepMode) {
            setTimeout(() => {
              if (
                !this.tourAfterStoreLinkingAddThreshold.isRunning() &&
                !this.hasSeenTourAfterStoreLinkingAddTreshold &&
                this.isFirstStoreOnboarding()
              ) {
                this.hasSeenTourAfterStoreLinkingAddTreshold = true;
                this.tourAfterStoreLinkingAddThreshold.start();
              }
            }, 500);
          }
        }),
        catchError((error) => {
          console.error("Error updating delivery:", error);
          this.setViewState(
            "form_submit_failed", `User attempted to submit delivery payload for store with id: ${this.local_data.id} but an error occurred: ${error.message}`,
          );
          return EMPTY;
        }),
        finalize(() => {
          this.loaderSvc.hide();
        }),
      )
      .subscribe();
  }

  submitThresholdStep() {
    if (this.thresholdForm.invalid) {
      return;
    }

    this.submitCount += 1;

    const thresholdPayload = this.claimTypesForProvider.reduce(
      (acc: Record<string, StoreThresholdPayload>, claimType) => {
        const group = this.thresholdForm.get(claimType.value) as FormGroup;
        acc[claimType.value] = {
          thresholdAmount: group.get("thresholdAmount")?.value,
          thresholdPercentage: group.get("thresholdPercentage")?.value,
        };
        return acc;
      },
      {},
    );

    this.loaderSvc.show();
    this.storeService
      .updateThreshold(this.local_data.storeId, thresholdPayload)
      .pipe(
        tap(() => {
          this.setViewState(
            "form_submitted", `User submitted threshold payload for store with id: ${this.local_data.id}`,
          );
          this.cacheFormValue("threshold", this.thresholdForm.value);
          this.thresholdSubmitted = true;

          // Close modal directly since delivery was already submitted
          this.dialogRef.close({ event: "Refresh" });
          this.setViewState(
            "modal_completed", `User closed the modal after submitting threshold payload for store with id: ${this.local_data.id}`,
          );
        }),

        catchError((error) => {
          console.error("Error updating threshold:", error);
          this.setViewState(
            "form_submit_failed", `User attempted to submit threshold payload for store with id: ${this.local_data.id} but an error occurred: ${error.message}`,
          );
          return EMPTY;
        }),
        finalize(() => {
          this.loaderSvc.hide();
        }),
      )
      .subscribe();
  }

  submitMultiStep() {
    // Validation depends on whether delivery step is enabled
    const deliveryFormValid = this.enableDeliveryAddressStep
      ? this.deliveryForm.valid
      : true;

    if (this.form.invalid || !deliveryFormValid || this.thresholdForm.invalid) {
      return;
    }

    this.submitCount += 1;

    const updatedStore: StoreUpdateDTO = {
      ...this.form.value,
    };

    const deliveryPayload: StoreDeliveryPayload = {
      city: this.deliveryForm.value.city,
      state: this.deliveryForm.value.state,
      postalCode: this.deliveryForm.value.postalCode,
    };

    const thresholdPayload = this.claimTypesForProvider.reduce(
      (acc: Record<string, StoreThresholdPayload>, claimType) => {
        const group = this.thresholdForm.get(claimType.value) as FormGroup;
        acc[claimType.value] = {
          thresholdAmount: group.get("thresholdAmount")?.value,
          thresholdPercentage: group.get("thresholdPercentage")?.value,
        };
        return acc;
      },
      {},
    );

    this.loaderSvc.show();

    // Build the observable chain based on enableDeliveryAddressStep
    let updateChain$ = this.storeService
      .update(this.local_data.storeId, updatedStore, {
        message: this.translate.instant("messages.Store Updated successfully!"),
        severity: "success",
        duration: 3000,
      })
      .pipe(
        switchMap(() => {
          // Only call updateDelivery if delivery step is enabled
          if (this.enableDeliveryAddressStep) {
            return this.storeService.updateDelivery(
              this.local_data.storeId,
              deliveryPayload,
            );
          }
          return of(null);
        }),
        switchMap(() =>
          this.storeService.updateThreshold(
            this.local_data.storeId,
            thresholdPayload,
          ),
        ),
        tap(() => {
          this.setViewState(
            "form_submitted", `User completed multi-step update for store with name: ${updatedStore.name}`,
          );
          this.cacheFormValue("store", this.form.value);
          if (this.enableDeliveryAddressStep) {
            this.cacheFormValue("delivery", this.deliveryForm.value);
          }
          this.cacheFormValue("threshold", this.thresholdForm.value);
          this.dialogRef.close({ event: "Refresh" });
          this.setViewState(
            "modal_completed", `User closed the update modal after completing multi-step update for store with name: ${updatedStore.name}`,
          );
        }),
        catchError((error) => {
          console.error("Error updating store in multi-step flow:", error);
          this.setViewState(
            "form_submit_failed", `User attempted to complete multi-step update for store with name: ${updatedStore.name} but an error occurred: ${error.message}`,
          );
          return EMPTY;
        }),
        finalize(() => {
          this.loaderSvc.hide();
        }),
      );

    updateChain$.subscribe();
  }

  private serializeFormValue(value: any): string {
    return JSON.stringify(this.sortObject(value));
  }

  private populateConfig(config?: StoreConfigResponse): void {
    if (!config) {
      return;
    }

    this.deliveryForm.patchValue(config.delivery || {});

    // Cache initial delivery when config exists so the user can advance to the next step
    if (config.delivery) {
      this.cacheFormValue("delivery", this.deliveryForm.value);
      // Mark as submitted to allow navigation without re-submission in onlyCompleteSetup mode
      this.deliverySubmitted = true;
    }

    Object.entries(config.thresholds || {}).forEach(([claimType, values]) => {
      const group = this.thresholdForm.get(claimType) as FormGroup;
      if (group) {
        group.patchValue({
          thresholdAmount: values.thresholdAmount,
          thresholdPercentage: values.thresholdPercentage,
        });
      }
    });
  }

  private hasStoredThresholdConfig(config?: StoreConfigResponse): boolean {
    return !!config && Object.keys(config.thresholds ?? {}).length > 0;
  }

  private sortObject(value: any): any {
    if (value === null || typeof value !== "object") {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.sortObject(item));
    }

    return Object.keys(value)
      .sort()
      .reduce((sorted: any, key) => {
        sorted[key] = this.sortObject(value[key]);
        return sorted;
      }, {});
  }

  private cacheFormValue(step: string, value: any) {
    const serialized = this.serializeFormValue(value);
    this.submitHistory.set(step, serialized);
  }

  private isFormValueCached(step: string, form: FormGroup): boolean {
    if (form.invalid) {
      return false;
    }
    const cached = this.submitHistory.get(step);
    if (!cached) {
      return false;
    }
    const serialized = this.serializeFormValue(form.value);
    return serialized === cached;
  }

  get isStoreFormCached(): boolean {
    return this.isFormValueCached("store", this.form);
  }

  get isDeliveryFormCached(): boolean {
    return this.isFormValueCached("delivery", this.deliveryForm);
  }

  get isThresholdFormCached(): boolean {
    return this.isFormValueCached("threshold", this.thresholdForm);
  }

  get hasPreviousStepForThreshold(): boolean {
    return this.showStoreStep || this.enableDeliveryAddressStep;
  }

  canCloseModal(): boolean {
    // Multistep flows (after linking / complete setup) must not show close.
    if (this.mode === "openAfterLinking" || this.mode === "onlyCompleteSetup") {
      return false;
    }

    return !isUserOnboardingInProgress(this.profileService.userProfile());
  }

  /** The guided tours only run while the user sets up their first store. */
  private isFirstStoreOnboarding(): boolean {
    return (
      (this.storeService.stores()?.length ?? 0) === 1 &&
      isUserOnboardingInProgress(this.profileService.userProfile())
    );
  }

  onCloseModal(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.tourAfterStoreLinking.stop();
    this.tourAfterStoreLinkingAddDelivery.stop();
    this.tourAfterStoreLinkingAddThreshold.stop();
  }
}
